import { Box, Button, Center, Loader, Stack, Text } from '@mantine/core';
import { GenericErrorPage } from 'src/error/GenericErrorPage';
import { useTranscript } from 'src/meetings/hooks/useTranscript';
import { useTranscriptAudio } from 'src/meetings/hooks/useTranscriptAudio';
import type { CalendarEvent } from 'src/meetings/models';
import PageLoader from 'src/UI/loader/PageLoader';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { AudioPlayer } from './transcript/AudioPlayer';
import { TranscriptSegmentsList } from './transcript/TranscriptSegmentsList';
import { TranscriptionStatus } from './transcript/types';

type Props = {
  event: CalendarEvent;
};

export const TranscriptTab = ({ event }: Props) => {
  const { transcript, isLoading, isError, error, refetch } = useTranscript({
    eventId: event.id,
    enabled: event.hasTranscription,
  });

  const audioControls = useTranscriptAudio({
    fileId: transcript?.file?.id,
    segments: transcript?.segments ?? [],
    eventId: event.id,
  });

  if (isLoading && event.hasTranscription) {
    return <PageLoader absolute={false} />;
  }

  // Handle errors - all errors including 404s indicate a problem since query only runs when hasTranscription=true
  if (isError) {
    return (
      <GenericErrorPage
        error={error}
        title="Unable to Load Transcript"
        text="We couldn't load the transcript for this meeting. Please try again later or contact support if the problem persists."
      />
    );
  }

  if (audioControls.isLoadingAudio && event.hasTranscription) {
    return <PageLoader absolute={false} />;
  }

  if (!event.hasTranscription || !transcript) {
    return (
      <Box p="md">
        <Text size="sm" c="gray.7">
          No recording available for this meeting.
        </Text>
      </Box>
    );
  }

  if (transcript) {
    if (transcript.status === TranscriptionStatus.PROCESSING) {
      return (
        <Center h="100%">
          <Stack align="center" gap="md">
            <Loader />
            <Text size="sm" c="gray.7" ta="center">
              Your recording is being transcribed.
            </Text>
            <Button onClick={() => refetch()} variant="light" size="sm">
              Check Status
            </Button>
          </Stack>
        </Center>
      );
    }

    if (transcript.status === TranscriptionStatus.FAILED) {
      return (
        <Center h="100%">
          <Stack align="center" gap="md" maw={400}>
            <Text size="sm" c="red.7" ta="center" fw={FONT_WEIGHTS.semibold}>
              Failed to process recording
            </Text>
            <Text size="sm" c="gray.7" ta="center">
              There was an error processing your recording. Please contact support for assistance.
            </Text>
          </Stack>
        </Center>
      );
    }

    if (transcript.status === TranscriptionStatus.EMPTY) {
      return (
        <Center h="100%">
          <Stack align="center" gap="md" maw={400}>
            <Text size="sm" c="gray.7" ta="center">
              No speech was detected in this recording.
            </Text>
          </Stack>
        </Center>
      );
    }

    if (transcript.status === TranscriptionStatus.READY) {
      // Defensive check: ensure required data exists
      if (!transcript.segments || !transcript.file) {
        return (
          <Center h="100%">
            <Stack align="center" gap="md" maw={400}>
              <Text size="sm" c="red.7" ta="center" fw={FONT_WEIGHTS.semibold}>
                Transcript data unavailable
              </Text>
              <Text size="sm" c="gray.7" ta="center">
                The transcript appears to be incomplete. Please contact support for assistance.
              </Text>
            </Stack>
          </Center>
        );
      }

      if (audioControls.isLoadingAudio) {
        return <PageLoader />;
      }

      return (
        <Box>
          <TranscriptSegmentsList
            segments={transcript.segments}
            selectedIndex={audioControls.selectedSegmentIndex ?? 0}
            onSegmentClick={audioControls.handleSegmentClick}
          />

          {!audioControls.hasAudioError && (
            <AudioPlayer
              isPlaying={audioControls.isPlaying}
              currentTime={audioControls.currentTime}
              duration={audioControls.duration}
              isMuted={audioControls.isMuted}
              onPlayPause={audioControls.handlePlayPause}
              onSeek={audioControls.handleSeek}
              onMuteToggle={audioControls.handleMuteToggle}
            />
          )}
        </Box>
      );
    }
  }

  // Fallback - shouldn't reach here
  return (
    <Box p="md">
      <Text size="sm" c="gray.7">
        No transcript available for this meeting.
      </Text>
    </Box>
  );
};
